import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  monitoringCountersService,
  mapMonitoringCounters,
  type LC_MonitoringFiltersIn,
  type MonitoringCounters,
} from '@/api/domains/attendance/monitoring';
import { useAbortController } from '@/hooks/ui/useAbortController';
import { useUI } from '@/hooks/ui/useUI';


export interface UseMonitoringCountersResult {
  counters: MonitoringCounters | null;
  loading: boolean;
  refetch: () => void;
}

// Loads the four monitoring counters (activos, ausentes, justificados, presentes)
// for the applied dimensional filters. Refetches whenever `appliedFilters` changes
// and exposes `refetch` for manual/auto refresh (ticket REXASIS-006).
export function useMonitoringCounters(
  appliedFilters: LC_MonitoringFiltersIn | null,
): UseMonitoringCountersResult {
  const { t: translate } = useTranslation();
  const { showApiError } = useUI();
  const { createSignal } = useAbortController();

  const [counters, setCounters] = useState<MonitoringCounters | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const fetchCounters = useCallback(async (): Promise<void> => {
    if (appliedFilters === null) {
      return;
    }

    setLoading(true);
    const signal = createSignal();
    const response = await monitoringCountersService.get(appliedFilters, signal);

    if (response.error?.code === 'ABORTED') {
      return;
    }

    if (!response.success || !response.data) {
      showApiError(response, translate('attendance:monitoring.counters.loadError'));
      setCounters(null);
      setLoading(false);
      return;
    }

    setCounters(mapMonitoringCounters(response.data));
    setLoading(false);
  }, [appliedFilters, createSignal, showApiError, translate]);

  useEffect(() => {
    void fetchCounters();
  }, [fetchCounters]);

  const refetch = useCallback((): void => {
    void fetchCounters();
  }, [fetchCounters]);

  return { counters, loading, refetch };
}

export default useMonitoringCounters;
